import { type ChangeEvent, type ReactNode, useId, useState } from 'react';

import { useAddTariffLines } from '@/shared/quotes/basket';
import { tariffDate } from '@/shared/tariffs/types';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PaperclipIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import { percent, unitPrice } from '@/shared/utils/money';

import {
  type ListChoice,
  chooseSupplierForRest,
  initialChoices,
  itemsToAdd,
  leftOut,
  quotable,
  restWithSupplier,
} from './listChoices';
import type { ListLine, ListResolution, Quote } from './types';
import { useResolveList } from './useQuotes';

/** The same ceiling as the back: a customer's list does not weigh more. */
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ACCEPT = '.xlsx,.xls,.csv,text/csv';

type Hit = NonNullable<ListLine['options']>[number];

function linesWord(count: number): string {
  return count === 1 ? 'línea' : 'líneas';
}

/** «/ 100 uds» when the price is not per one. */
function per(unit: number | null | undefined): string {
  return unit && unit !== 1 ? ` / ${unit} uds` : '';
}

/**
 * One article as the list shows it: its reference and description, then whose tariff it
 * is and its price. Only what the profile may see came from the back; the cost never does
 * here, not even for whoever may see it: a list is answered at the counter.
 */
function HitText({ hit, withSupplier = true }: { hit: Hit; withSupplier?: boolean }) {
  const price =
    hit.list_price !== null && hit.list_price !== undefined
      ? `PVP ${unitPrice(hit.list_price)}${per(hit.price_unit)}${
          hit.usual_discount_pct ? ` · ${percent(hit.usual_discount_pct)} de dto.` : ''
        }`
      : 'Sin PVP en la tarifa';
  return (
    <span className="flex min-w-0 flex-col">
      <span className="text-ink text-[14px]">
        <span className="font-medium">{hit.reference}</span>
        {hit.description ? <span className="text-ink-2"> · {hit.description}</span> : null}
      </span>
      <span className="text-ink-3 text-[12.5px]">
        {withSupplier ? `${hit.supplier_name} · ` : ''}
        {hit.tariff_date ? `tarifa del ${tariffDate(hit.tariff_date)} · ` : ''}
        {price}
      </span>
      {quotable(hit) ? null : (
        <span className="text-ink-3 text-[12.5px]">
          Su precio sale del de compra: lo presupuesta quien lo ve.
        </span>
      )}
    </span>
  );
}

/**
 * «Pegar lista» (contract v10.52, ADR 0046 of the back): the customer's list, pasted from
 * their email or their Excel, or the file itself, looked up in the tariffs in force; the
 * person goes through the answer and the chosen lines go to the end of this draft. Every
 * line of the list is shown somewhere, the ones that are not articles too: a list where a
 * line silently vanished is a quote short of something.
 */
export function PasteList({ quote, onClose }: { quote: Quote; onClose: () => void }) {
  const resolve = useResolveList();
  const add = useAddTariffLines(quote.id);
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<ListResolution | null>(null);
  const [choices, setChoices] = useState<ListChoice[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const textId = useId();
  const fileId = useId();

  function search(source: string | File): void {
    setProblem(null);
    setNote(null);
    resolve.mutate(source, {
      onSuccess: (answer) => {
        setResult(answer);
        setChoices(initialChoices(answer.lines ?? []));
      },
    });
  }

  function onFile(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setProblem('El fichero pesa más de 2 MB: una lista de un cliente no pesa tanto.');
      return;
    }
    search(file);
  }

  if (!result) {
    return (
      <Dialog
        // A key for each view: the answer is a new dialog, so the focus starts again at its
        // top and not on the button that took the place of «Buscar en las tarifas».
        key="lista"
        title="Pegar lista"
        description="La lista del cliente, buscada en las tarifas vigentes."
        size="lg"
        onClose={onClose}
        busy={resolve.isPending}
        footer={
          <>
            <Button variant="ghost" onClick={onClose} disabled={resolve.isPending}>
              Cancelar
            </Button>
            <Button onClick={() => search(text)} disabled={text.trim() === '' || resolve.isPending}>
              {resolve.isPending ? 'Buscando…' : 'Buscar en las tarifas'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <label htmlFor={textId} className="text-ink text-[14px] font-medium">
            Copia la lista del correo del cliente o de su Excel y pégala aquí
          </label>
          <textarea
            id={textId}
            className="field min-h-48 py-2 font-mono text-[13.5px]"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={'5 x 0602025012\n2603014003   10\n8412345678901 2'}
            spellCheck={false}
          />
          <p className="text-ink-3 text-[12.5px] leading-4">
            Una referencia o un código de barras por línea, con la cantidad delante o detrás. Sin
            cantidad se pone 1. Los saludos y la firma se enseñan aparte, no se pierden.
          </p>
          <p className="text-ink-2 flex items-center gap-1.5 text-[14px]">
            <PaperclipIcon size={15} className="text-ink-3" />
            <span>
              O{' '}
              <label htmlFor={fileId} className="link cursor-pointer font-medium">
                sube su Excel o su CSV
              </label>{' '}
              (hasta 2 MB).
            </span>
            <input
              id={fileId}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={onFile}
              disabled={resolve.isPending}
            />
          </p>
          {problem ? (
            <p role="alert" className="text-danger text-[14px]">
              {problem}
            </p>
          ) : null}
          {resolve.isError ? (
            <ErrorMessage error={resolve.error}>No se pudo buscar la lista</ErrorMessage>
          ) : null}
        </div>
      </Dialog>
    );
  }

  const lines = result.lines ?? [];
  const items = itemsToAdd(choices);
  const missing = leftOut(lines, choices);
  const indices = (status: ListLine['status']) =>
    lines.flatMap((line, index) => (line.status === status ? [index] : []));
  const found = indices('found');
  const choose = indices('choose');
  const notFound = indices('not_found');
  const unread = indices('unread');

  function update(index: number, change: Partial<ListChoice>): void {
    setNote(null);
    setChoices((current) =>
      current.map((choice, at) => (at === index ? { ...choice, ...change } : choice)),
    );
  }

  function pickForRest(supplierId: string, supplierName: string): void {
    const next = chooseSupplierForRest(lines, choices, supplierId);
    setChoices(next.choices);
    setNote(`${supplierName} elegido en ${next.chosen} ${linesWord(next.chosen)} más de la lista.`);
  }

  function submit(): void {
    if (items.length === 0) return;
    add.mutate(items, { onSuccess: onClose });
  }

  function row(index: number, body: ReactNode): ReactNode {
    const line = lines[index];
    const choice = choices[index];
    if (!line || !choice) return null;
    // The row too: a list that repeats a reference would otherwise have two controls
    // with one name.
    const label = `${line.reference ?? line.text} (línea ${line.row})`;
    const blocked = !(line.options ?? []).some(quotable);
    return (
      <li
        key={index}
        className={cn(
          'border-line-2 flex items-start gap-2.5 rounded-md border px-3 py-2',
          !choice.include && 'opacity-60',
        )}
      >
        <input
          type="checkbox"
          className="mt-1 size-4 shrink-0"
          checked={choice.include}
          onChange={(event) => update(index, { include: event.target.checked })}
          disabled={blocked}
          aria-label={`Añadir ${label}`}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-ink-3 truncate text-[12px]" title={line.text}>
            Línea {line.row}: «{line.text}»
          </span>
          {body}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <input
            aria-label={`Cantidad de ${label}`}
            className="field num h-8 w-16 px-1.5 text-[13.5px]"
            inputMode="decimal"
            value={choice.quantity}
            onChange={(event) => update(index, { quantity: event.target.value })}
            disabled={!choice.include}
          />
          {line.quantity_guessed ? (
            <span className="text-ink-3 text-[11.5px]">sin cantidad</span>
          ) : null}
        </div>
      </li>
    );
  }

  return (
    <Dialog
      key="respuesta"
      title="Pegar lista"
      description={`${found.length} encontradas · ${choose.length} para elegir proveedor · ${notFound.length} no encontradas${unread.length ? ` · ${unread.length} aparte` : ''}`}
      size="lg"
      onClose={onClose}
      busy={add.isPending}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              setResult(null);
              add.reset();
            }}
            disabled={add.isPending}
          >
            Volver a la lista
          </Button>
          <Button onClick={submit} disabled={items.length === 0 || add.isPending}>
            {add.isPending
              ? 'Añadiendo…'
              : `Añadir ${items.length} ${linesWord(items.length)} al presupuesto`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {result.limited ? (
          <Callout tone="warn" compact>
            La lista es demasiado larga: solo se ha leído el principio (500 referencias o 3000
            líneas).
          </Callout>
        ) : null}
        {lines.length === 0 ? (
          <Callout tone="neutral">No hay ninguna línea en lo que has pegado.</Callout>
        ) : null}

        {choose.length > 0 ? (
          <section className="flex flex-col gap-2" aria-label="Elige el proveedor">
            <h3 className="text-ink text-[14px] font-semibold">Elige el proveedor</h3>
            <p className="text-ink-3 -mt-1 text-[12.5px] leading-4">
              Están en más de una tarifa, o se han leído de una frase del correo: elige el artículo,
              o márcalo si es lo que pide el cliente.
            </p>
            <ul className="flex flex-col gap-2">
              {choose.map((index) => {
                const line = lines[index];
                const chosen = line?.options?.find((hit) => hit.id === choices[index]?.itemId);
                const rest = chosen
                  ? restWithSupplier(lines, choices, chosen.supplier_id, index)
                  : 0;
                return row(
                  index,
                  <fieldset className="flex flex-col gap-1.5">
                    <legend className="sr-only">
                      Proveedor de {line?.reference ?? line?.text}
                    </legend>
                    {(line?.options ?? []).map((hit) => (
                      <label key={hit.id} className="flex cursor-pointer items-start gap-2">
                        <input
                          type="radio"
                          className="mt-1 shrink-0"
                          name={`proveedor-${index}`}
                          checked={choices[index]?.itemId === hit.id}
                          disabled={!quotable(hit)}
                          onChange={() => update(index, { itemId: hit.id, include: true })}
                        />
                        <HitText hit={hit} />
                      </label>
                    ))}
                    {chosen && rest > 0 ? (
                      <button
                        type="button"
                        className="link self-start text-[13px]"
                        onClick={() => pickForRest(chosen.supplier_id, chosen.supplier_name)}
                      >
                        Usar {chosen.supplier_name} para el resto de la lista ({rest}{' '}
                        {linesWord(rest)})
                      </button>
                    ) : null}
                  </fieldset>,
                );
              })}
            </ul>
            {note ? (
              <p role="status" className="text-ink-2 text-[13px]">
                {note}
              </p>
            ) : null}
          </section>
        ) : null}

        {found.length > 0 ? (
          <section className="flex flex-col gap-2" aria-label="Encontradas">
            <h3 className="text-ink text-[14px] font-semibold">Encontradas</h3>
            <ul className="flex flex-col gap-2">
              {found.map((index) => {
                const hit = lines[index]?.options?.[0];
                return row(index, hit ? <HitText hit={hit} /> : null);
              })}
            </ul>
          </section>
        ) : null}

        {notFound.length > 0 ? (
          <section className="flex flex-col gap-1.5" aria-label="No encontradas">
            <h3 className="text-ink text-[14px] font-semibold">No encontradas</h3>
            <p className="text-ink-3 text-[12.5px] leading-4">
              Ninguna tarifa vigente tiene estas referencias. Búscalas en Documentos por su
              descripción, o añádelas a mano con «Añadir línea».
            </p>
            <ul className="text-ink-2 flex flex-col gap-0.5 text-[13.5px]">
              {notFound.map((index) => (
                <li key={index}>
                  Línea {lines[index]?.row}: «{lines[index]?.text}»
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {unread.length > 0 ? (
          <details className="group text-[13.5px]">
            <summary className="text-ink-3 hover:text-ink cursor-pointer font-medium select-none">
              {unread.length} {linesWord(unread.length)} que no parecen artículos
            </summary>
            <ul className="text-ink-3 mt-1.5 flex flex-col gap-0.5">
              {unread.map((index) => (
                <li key={index} className="truncate" title={lines[index]?.text}>
                  Línea {lines[index]?.row}: «{lines[index]?.text}»
                </li>
              ))}
            </ul>
          </details>
        ) : null}

        {missing.unchosen > 0 || missing.badQuantity > 0 ? (
          <p role="status" className="text-warn text-[13px]">
            {missing.unchosen > 0
              ? `${missing.unchosen} ${linesWord(missing.unchosen)} sin proveedor elegido no se ${missing.unchosen === 1 ? 'añadirá' : 'añadirán'}. `
              : ''}
            {missing.badQuantity > 0
              ? `${missing.badQuantity} ${linesWord(missing.badQuantity)} sin una cantidad válida no se ${missing.badQuantity === 1 ? 'añadirá' : 'añadirán'}.`
              : ''}
          </p>
        ) : null}
        {add.isError ? (
          <ErrorMessage error={add.error}>No se pudieron añadir las líneas</ErrorMessage>
        ) : null}
      </div>
    </Dialog>
  );
}
